import * as React from "react"
import { forwardRef } from "react"
import styled from "styled-components"
import { ChevronDown } from "lucide-react"

type SelectHeight = "large" | "small"

const SelectWrapper = styled.div<{ $fullWidth?: boolean }>`
  position: relative;
  display: inline-block;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "21.875rem")};
`

const StyledSelect = styled.select<{
  $isInError: boolean
  $fullWidth?: boolean
  $height: SelectHeight
}>`
  color: ${({ theme }) => theme.colors.grey.$600};

  border: 1px solid ${({ theme, $isInError }) => ($isInError ? theme.colors.danger.$200 : theme.colors.grey.$200)};
  outline: 0 solid ${({ theme }) => theme.colors.grey.$400};
  box-shadow: ${({ theme }) => theme.shadows.light};
  border-radius: 0.5rem;

  height: ${({ $height }) => ($height === "small" ? "2rem" : "2.5rem")};
  width: 100%;
  padding: 0 1.75rem 0 0.5rem;

  transition: all 0.2s ease;
  box-sizing: border-box;

  appearance: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey.$400};
  }

  &:active {
    border: 1px solid ${({ theme }) => theme.colors.grey.$600};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.grey.$400};
    border: 1px solid ${({ theme }) => theme.colors.grey.$200};
  }

  &:disabled {
    border: 1px solid ${({ theme }) => theme.colors.grey.$200};
    background-color: ${({ theme }) => theme.colors.grey.$200};
  }
`

const IconWrapper = styled.div`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${({ theme }) => theme.colors.grey.$600};
`

type SelectProps = {
  $isInError: boolean
  $fullWidth?: boolean
  $height?: SelectHeight
  children?: React.ReactNode
  as?: React.ElementType
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "as">

export const Select = forwardRef<HTMLElement, SelectProps>(
  ({ $isInError, $fullWidth, $height = "large", children, ...rest }, ref) => (
    <SelectWrapper $fullWidth={$fullWidth}>
      <StyledSelect ref={ref} $isInError={$isInError} $fullWidth={$fullWidth} $height={$height} {...rest}>
        {children}
      </StyledSelect>
      <IconWrapper>
        <ChevronDown size={16} />
      </IconWrapper>
    </SelectWrapper>
  ),
)

Select.displayName = "Select"
